import { logger } from "@/lib/logger";

/**
 * Detect if the current device is running Android
 */
export function isAndroidDevice(): boolean {
  if (typeof navigator === "undefined") return false;
  return /android/i.test(navigator.userAgent);
}

/**
 * Encode ESC/POS content to Base64 for RawBT URL scheme
 */
function encodeForRawBT(content: string): string {
  try {
    // Encode to Base64 handling UTF-8 characters and ESC/POS control chars
    return btoa(unescape(encodeURIComponent(content)));
  } catch (e) {
    logger.error("[RawBT] Error encoding content:", e);
    throw new Error("Falha ao codificar conteúdo para impressão");
  }
}

/**
 * Send content to RawBT app via URL scheme.
 * RawBT receives ESC/POS data and routes to the configured printer (USB, Bluetooth, or Network).
 * 
 * The RawBT app must be installed on the Android device.
 * Download: https://play.google.com/store/apps/details?id=ru.a402d.rawbtprinter
 * 
 * Important: Each call briefly switches focus from the browser to the RawBT app.
 */
export function printViaRawBT(content: string): boolean {
  try {
    if (!content || content.trim().length === 0) {
      logger.warn("[RawBT] Empty content, skipping");
      return false;
    }

    const base64Content = encodeForRawBT(content);
    const rawbtUrl = `rawbt:base64,${base64Content}`;

    logger.log(`[RawBT] Sending print job (${base64Content.length} chars base64)`);

    // Use window.location.href to trigger the intent
    // This will briefly switch to RawBT app which processes and returns
    window.location.href = rawbtUrl;

    return true;
  } catch (error) {
    logger.error("[RawBT] Error sending print job:", error);
    return false;
  }
}

/**
 * Send multiple print jobs to RawBT with a small delay between each
 * to avoid overwhelming the intent handler.
 */
export async function printMultipleViaRawBT(contents: string[]): Promise<boolean> {
  if (contents.length === 0) return true;

  let success = true;
  for (let i = 0; i < contents.length; i++) {
    const result = printViaRawBT(contents[i]);
    if (!result) {
      success = false;
    }
    // Small delay between jobs to let RawBT process each one
    if (i < contents.length - 1) {
      await new Promise(resolve => setTimeout(resolve, 1500));
    }
  }
  return success;
}

/**
 * Print a test page via RawBT to verify the connection
 */
export function printRawBTTestPage(): boolean {
  const testContent = [
    "\x1B\x40",           // ESC @ - Initialize printer
    "\x1B\x61\x01",       // ESC a 1 - Center align
    "\x1B\x45\x01",       // ESC E 1 - Bold ON
    "TESTE DE IMPRESSAO\n",
    "\x1B\x45\x00",       // ESC E 0 - Bold OFF
    "================================\n",
    "\x1B\x61\x00",       // ESC a 0 - Left align
    "RawBT conectado com sucesso!\n",
    "Dispositivo: Android\n",
    `Data: ${new Date().toLocaleString("pt-BR")}\n`,
    "================================\n",
    "\x1B\x61\x01",       // Center
    "Altus Food\n",
    "\x1B\x61\x00",       // Left
    "\n\n\n",
  ].join("");

  return printViaRawBT(testContent);
}
